// src/services/loginTwoFactorClient.ts — the two calls behind the sign-in code
// screen: POST /auth/sign-in (a pendingId for the console's current bearer) and
// POST /auth/verify-2fa (the code), as the api's sign-in two-factor contract
// defines them.
//
// A dedicated `axios.create()` client, like services/twoFactorClient.ts, for
// two reasons. The shared interceptor drops `details` (the screen needs
// `retryAfterSeconds` to say how long to wait), and it would answer a refusal
// on THESE calls by opening the very screen that is making them. It takes the
// bearer from the token provider (through sharedRequestHeaders) and the base
// URL from `axios.defaults` at request time, so it always presents the bearer
// the console holds, the one being cleared.
import axios from "axios";
import { sharedRequestHeaders } from "./apiService";

const client = axios.create({ timeout: 20_000 });

/** What POST /auth/verify-2fa answers on the bearer path. It never carries a token. */
export interface LoginTwoFactorVerified {
  verified: true;
  factor?: "totp" | "device" | "recovery_code";
  path?: "bearer" | "password";
  /** When the clearance ends: the cleared token's expiry. */
  expiresAt?: string;
  /** Only after a recovery-code sign-in: how many codes are left. */
  recoveryCodesRemaining?: number;
}

/**
 * Why a call failed, in the terms the screen shows:
 *   wrong-code   401 INVALID_TWO_FACTOR_CODE
 *   locked       429 TWO_FACTOR_TOO_MANY_ATTEMPTS (wait `retryAfterSeconds`)
 *   expired      401 with no code: the pendingId expired or was already used
 *   unreachable  no answer, a timeout, or a 5xx
 *   refused      anything else the api said no to
 */
export type LoginTwoFactorFailureKind =
  | "wrong-code"
  | "locked"
  | "expired"
  | "unreachable"
  | "refused";

export class LoginTwoFactorError extends Error {
  readonly kind: LoginTwoFactorFailureKind;
  readonly retryAfterSeconds?: number;

  constructor(
    kind: LoginTwoFactorFailureKind,
    message: string,
    retryAfterSeconds?: number
  ) {
    super(message);
    this.name = "LoginTwoFactorError";
    this.kind = kind;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
  code?: string;
  details?: { retryAfterSeconds?: unknown };
}

/** `Retry-After` is either seconds or an HTTP date. */
const parseRetryAfterHeader = (value: unknown): number | undefined => {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const at = Date.parse(value);
  return Number.isNaN(at)
    ? undefined
    : Math.max(0, Math.ceil((at - Date.now()) / 1000));
};

const toFailure = (error: unknown): LoginTwoFactorError => {
  if (error instanceof LoginTwoFactorError) return error;
  if (!axios.isAxiosError<Envelope<unknown>>(error) || !error.response) {
    return new LoginTwoFactorError("unreachable", "No answer from the api.");
  }
  const { status, data, headers } = error.response;
  const code = typeof data?.code === "string" ? data.code : undefined;
  const message = data?.message || error.message;
  if (status >= 500) return new LoginTwoFactorError("unreachable", message);
  if (status === 429 || code === "TWO_FACTOR_TOO_MANY_ATTEMPTS") {
    const fromDetails = data?.details?.retryAfterSeconds;
    const retryAfter =
      typeof fromDetails === "number" && Number.isFinite(fromDetails)
        ? Math.max(0, Math.ceil(fromDetails))
        : parseRetryAfterHeader(
            (headers as Record<string, unknown> | undefined)?.["retry-after"]
          );
    return new LoginTwoFactorError("locked", message, retryAfter);
  }
  if (status === 401 && code === "INVALID_TWO_FACTOR_CODE") {
    return new LoginTwoFactorError("wrong-code", message);
  }
  if (status === 401 && !code) {
    return new LoginTwoFactorError("expired", message);
  }
  return new LoginTwoFactorError("refused", message);
};

const post = async <T>(url: string, body?: object): Promise<T> => {
  try {
    const response = await client.request<Envelope<T>>({
      method: "POST",
      url,
      baseURL: axios.defaults.baseURL,
      // The same transport as the console's own requests.
      adapter: axios.defaults.adapter,
      data: body,
      headers: await sharedRequestHeaders(),
    });
    const envelope = response.data;
    if (envelope?.success === false) {
      throw new LoginTwoFactorError(
        "refused",
        envelope.message || "Request failed"
      );
    }
    return envelope?.data as T;
  } catch (error) {
    throw toFailure(error);
  }
};

/**
 * Ask the api for a pending sign-in for the bearer the console already holds.
 * No credentials in the body: the bearer IS the sign-in.
 */
export const startLoginTwoFactor = async (): Promise<string> => {
  const data = await post<{ requiresTwoFA?: boolean; pendingId?: unknown }>(
    "/api/v2/auth/sign-in"
  );
  if (typeof data?.pendingId !== "string" || !data.pendingId) {
    throw new LoginTwoFactorError(
      "refused",
      "The sign-in answer carried no pending sign-in."
    );
  }
  return data.pendingId;
};

/** Send the code (a 6-digit code or a recovery code, both in `otpCode`). */
export const verifyLoginTwoFactor = async (
  pendingId: string,
  otpCode: string
): Promise<LoginTwoFactorVerified> => {
  const data = await post<Partial<LoginTwoFactorVerified>>(
    "/api/v2/auth/verify-2fa",
    { pendingId, otpCode }
  );
  if (data?.verified !== true) {
    throw new LoginTwoFactorError(
      "refused",
      "The code was not confirmed by the api."
    );
  }
  return {
    verified: true,
    factor: data.factor,
    path: data.path,
    expiresAt: data.expiresAt,
    recoveryCodesRemaining:
      typeof data.recoveryCodesRemaining === "number"
        ? data.recoveryCodesRemaining
        : undefined,
  };
};
